import { Component, inject, Injectable, Input, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { DateRange, MAT_DATE_RANGE_SELECTION_STRATEGY, MatDateRangeSelectionStrategy } from '@angular/material/datepicker';
import { FormControl, FormGroup } from '@angular/forms';

/* Custom date range selection strategy for a 7-day range (See angular material docs for more information) */
@Injectable()
export class SevenDayRangeSelectionStrategy<D> implements MatDateRangeSelectionStrategy<D> {
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter<D>);

  selectionFinished(date: D | null): DateRange<D> {
    return this._createSevenDayRange(date);
  }

  createPreview(activeDate: D | null): DateRange<D> {
    return this._createSevenDayRange(activeDate);
  }

  private _createSevenDayRange(date: D | null): DateRange<D> {
    if (date) {
      const start = date;
      const end = this._dateAdapter.addCalendarDays(date, 6);
      return new DateRange<D>(start, end);
    }

    return new DateRange<D>(null, null);
  }
}

/* Date range picker component */
@Component({
  selector: 'app-date-range-picker',
  standalone: false,
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: SevenDayRangeSelectionStrategy },
  ],
})
export class DateRangePickerComponent implements OnInit {

  // Input property for customizing the dates inside the date range picker
  @Input() dateClass!: (date: Date) => string[];

  @Output() dateRangeChanged = new EventEmitter<{ start: Date | null; end: Date | null }>();
  @Output() datesGenerated = new EventEmitter<Date[]>();

  dateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  ngOnInit(): void {
    this.initializeCurrentWeekRange();
    this.updateDisplayedDates();

    // Subscribe to range changes
    this.dateRange.valueChanges.subscribe((value) => {
      this.dateRangeChanged.emit({
        start: value.start ?? null,
        end: value.end ?? null
      });
      this.updateDisplayedDates();
    });
  }

  goForwardAWeek() {
    const start = this.dateRange.value.start;
    const end = this.dateRange.value.end;

    if (start && end) {
      const newStart = new Date(start);
      newStart.setDate(newStart.getDate() + 7);
      const newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() + 7);

      this.dateRange.patchValue({
        start: newStart,
        end: newEnd
      });
    }
  }

  goBackAWeek() {
    const start = this.dateRange.value.start;
    const end = this.dateRange.value.end;

    if (start && end) {
      const newStart = new Date(start);
      newStart.setDate(newStart.getDate() - 7);
      const newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() - 7);

      this.dateRange.patchValue({
        start: newStart,
        end: newEnd
      });
    }
  }

  initializeCurrentWeekRange() {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    this.dateRange.patchValue({
      start: startOfWeek,
      end: endOfWeek
    });
  }

  private updateDisplayedDates() {
    const dates: Date[] = [];
    const startDate = this.dateRange.value.start;
    const endDate = this.dateRange.value.end;

    if (startDate && endDate) {
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d));
      }
    }
    this.datesGenerated.emit(dates);
  }

  futureDayFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    return date <= today;
  };
}